/** Registration value section — "REGISTER ONCE. ACCESS EVERYTHING." with three-column layout. */

import { Fragment, useCallback, useRef, useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { useInView } from 'react-intersection-observer';
import { ScrollReveal } from './components/ScrollReveal';
import { HDEDesktopFrame } from './components/HDEDesktopFrame';

const COLUMN_1_ITEMS = [
    {
        title: 'Exhibitor Previews',
        description:
            "Browse every exhibitor's products and portfolio on ArchiPro before you arrive.",
    },
    {
        title: 'Download the ArchiPro App',
        description:
            'Your Home Design Evening ticket will be available in the ArchiPro App one week before the event.',
    },
    {
        title: 'Start Your Design Board',
        description:
            'Save projects, professionals and products that catch your eye.',
    },
];

const COLUMN_2_ITEMS = [
    {
        title: 'Digital Ticket',
        description:
            'Your ticket lives in your ArchiPro App. Scan your ticket at the door.',
    },
    {
        title: 'Exhibitor Scanner',
        description:
            'Connect with exhibitors by scanning QR codes to save their products and contact details instantly.',
    },
    {
        title: 'Watch the Live Panel',
        description:
            'Learn the decisions pros make before building that save you thousands.',
    },
];

const COLUMN_3_ITEMS = [
    {
        title: 'Everything Saved',
        description:
            'Every product you scanned, every professional you met, all in your ArchiPro account.',
    },
    {
        title: 'One-Click Enquiry',
        description:
            'Send your brief to any professional you met. They get your scope, budget and design board.',
    },
    {
        title: 'Product Research',
        description:
            'Full specs, pricing, and comparison tools for everything you discovered.',
    },
    {
        title: 'Your Building Companion',
        description:
            'ArchiPro guides your project from inspiration to completion. The event is just the start.',
    },
];

interface ItemListProps {
    items: { title: string; description: string }[];
}

const ItemList = ({ items }: ItemListProps) => (
    <ul
        className="mt-12 flex flex-col gap-[1.875rem]"
        style={{ maxWidth: '28.25rem' }}
    >
        {items.map((item) => (
            <li key={item.title} className="flex flex-col">
                <p className="font-sans text-[1.125rem] font-medium leading-tight text-black">
                    {item.title}
                </p>
                <p className="font-sans text-[1.125rem] font-medium leading-tight text-[#818585]">
                    {item.description}
                </p>
            </li>
        ))}
    </ul>
);

interface RegistrationAssets {
    beforeEvening: string;
    onTheNight: string;
    afterEvening: string;
}

const COLUMNS: {
    titleLines: string[];
    imageKey: keyof RegistrationAssets;
    imageAlt: string;
    imageRounded?: boolean;
    items: { title: string; description: string }[];
    width?: string;
}[] = [
    {
        titleLines: ['Before the', 'Evening'],
        imageKey: 'beforeEvening',
        imageAlt: 'Before the evening',
        imageRounded: true,
        items: COLUMN_1_ITEMS,
        width: '42rem',
    },
    {
        titleLines: ['On the Night'],
        imageKey: 'onTheNight',
        imageAlt: 'On the night',
        items: COLUMN_2_ITEMS,
        width: '38.5rem',
    },
    {
        titleLines: ['After the', 'Evening'],
        imageKey: 'afterEvening',
        imageAlt: 'After the evening',
        items: COLUMN_3_ITEMS,
    },
];

interface RegistrationValueProps {
    assets: RegistrationAssets;
    onRegister?: () => void;
    showRegisterButton?: boolean;
    isMobile?: boolean;
}

export const RegistrationValue = ({
    assets,
    onRegister,
    showRegisterButton = true,
    isMobile = false,
}: RegistrationValueProps) => {
    const { ref: sectionRef, inView } = useInView({
        triggerOnce: true,
        threshold: 0.15,
    });
    const trackRef = useRef<HTMLDivElement>(null);
    const [activeSlide, setActiveSlide] = useState(0);

    const handleTrackScroll = useCallback(() => {
        if (!trackRef.current || !trackRef.current.firstElementChild) {
            return;
        }
        const slideWidth =
            trackRef.current.firstElementChild.getBoundingClientRect().width;
        if (!slideWidth) {
            return;
        }
        setActiveSlide(Math.round(trackRef.current.scrollLeft / slideWidth));
    }, []);

    if (isMobile) {
        return (
            <section
                ref={sectionRef}
                data-testid="registration-value"
                className="relative mt-8 w-full overflow-visible border-t border-[#dbdbdb] bg-white pb-[2.25rem] pt-12"
            >
                <div className="px-[1.5rem]">
                    <h2 className="pt-0.5 font-display text-[2.5rem] font-bold leading-none tracking-[-0.02em] text-[#141414]">
                        REGISTER
                        <br />
                        ONCE.
                    </h2>
                    <p className="font-ghost text-[2.375rem] leading-none tracking-[-0.02em] text-[#141414]">
                        ACCESS
                        <br />
                        EVERYTHING.
                    </p>
                    <p className="mt-[2rem] text-[0.9375rem] leading-[1.33] text-[#141414]">
                        Your HDE registration creates your ArchiPro account,
                        unlocking the full ecosystem for your building project,
                        before, during and long after the evening.
                    </p>
                </div>

                <div
                    ref={trackRef}
                    onScroll={handleTrackScroll}
                    className="scrollbar-hide hde-mobile-carousel mt-[1.625rem] snap-x snap-mandatory overflow-x-auto"
                >
                    <div className="flex gap-[1.75rem] px-[1.5rem]">
                        {COLUMNS.map((col) => (
                            <div
                                key={col.imageKey}
                                className="w-[20.375rem] shrink-0 snap-start border-l border-[#dbdbdb] pl-[1.5rem] pt-[0.15rem]"
                            >
                                <h3 className="font-ghost text-[1.625rem] leading-[0.92] tracking-[-0.02em] text-[#141414]">
                                    {col.titleLines.join(' ')}
                                </h3>
                                <img
                                    src={assets[col.imageKey]}
                                    alt={col.imageAlt}
                                    className="mt-8 h-[10.375rem] w-[17.875rem] rounded-[0.25rem] object-cover"
                                />
                                <ul className="mt-[1.25rem] space-y-[0.75rem]">
                                    {col.items.map((item) => (
                                        <li key={item.title}>
                                            <p className="text-[0.8125rem] font-medium leading-[1.385] text-[#141414]">
                                                {item.title}
                                            </p>
                                            <p className="text-[0.8125rem] leading-[1.385] text-[#9a9a9a]">
                                                {item.description}
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mt-[1.375rem] flex justify-center gap-[0.375rem]">
                    {COLUMNS.map((_, index) => (
                        <div
                            key={index}
                            className={cn(
                                'h-[0.5rem] w-[0.5rem] rounded-full transition-colors',
                                activeSlide === index
                                    ? 'bg-[#141414]'
                                    : 'bg-[#d9d9d9]',
                            )}
                        />
                    ))}
                </div>

                <div className="px-[1.5rem] pt-[1.375rem]">
                    <button
                        type="button"
                        onClick={onRegister}
                        className={`flex h-[3.25rem] w-full items-center justify-center rounded-full bg-[#141414] ${showRegisterButton ? '' : 'pointer-events-none invisible'}`}
                    >
                        <span className="text-[0.9375rem] text-white">
                            Register Now — Free
                        </span>
                    </button>
                </div>
            </section>
        );
    }

    return (
        <section
            ref={sectionRef}
            data-testid="registration-value"
            className="relative w-full bg-white pt-[13.5rem]"
        >
            <HDEDesktopFrame>
                {/* Staggered heading — REGISTER ONCE. bold left, ACCESS EVERYTHING. indented so "A" aligns under "G" */}
                <div className="pl-[21.49625rem]">
                    <ScrollReveal animation="fade-up" duration={700}>
                        <h2 className="font-display text-[5.981rem] font-bold leading-[0.9] tracking-[-0.22rem] text-[#0b0a09]">
                            REGISTER ONCE.
                        </h2>
                    </ScrollReveal>
                    <ScrollReveal
                        animation="fade-up"
                        duration={700}
                        delay={200}
                    >
                        <p
                            className="mt-[0.75rem] font-ghost text-[5.981rem] leading-[0.9] tracking-[-0.2rem] text-[#0b0a09]"
                            style={{
                                paddingLeft: '7.85rem',
                            }}
                        >
                            ACCESS EVERYTHING.
                        </p>
                    </ScrollReveal>

                    {/* Description — left edge aligned with "ACCESS EVERYTHING." */}
                    <ScrollReveal
                        animation="fade-up"
                        duration={400}
                        delay={500}
                    >
                        <p
                            className="mt-8.75 font-sans text-[1rem] font-medium leading-[1.2] text-black"
                            style={{
                                marginLeft: '8.5rem',
                                width: '39rem',
                                maxWidth: '80%',
                                letterSpacing: '-0.003rem',
                            }}
                        >
                            Your HDE registration creates your ArchiPro account
                            — unlocking the full ecosystem for your building
                            project, before, during and long after the evening.
                        </p>
                    </ScrollReveal>
                </div>

                {/* Three-column layout with vertical line separators */}
                <div className="mx-[2.5rem] mt-[7.8rem] flex">
                    {COLUMNS.map((col, index) => (
                        <ScrollReveal
                            key={col.imageKey}
                            animation="fade-up"
                            duration={600}
                            delay={index * 150}
                            className={cn(col.width ? 'shrink-0' : 'flex-1')}
                            style={col.width ? { width: col.width } : undefined}
                        >
                            <div
                                className={cn(
                                    'relative pb-10 pl-[1.875rem]',
                                    col.width ? 'shrink-0' : 'flex-1',
                                )}
                            >
                                <div
                                    className={cn(
                                        'absolute bottom-0 left-0 top-0 w-px origin-top bg-black/20 transition-transform duration-500 ease-out',
                                        inView ? 'scale-y-100' : 'scale-y-0',
                                    )}
                                    style={{
                                        transitionDelay: `${index * 150}ms`,
                                    }}
                                />
                                <h3
                                    className={cn(
                                        'font-ghost text-[2.769rem] leading-[0.9] tracking-[-0.083rem] text-[#0b0a09]',
                                        col.titleLines.length === 1 &&
                                            'min-h-[5rem]',
                                    )}
                                >
                                    {col.titleLines.map((line, i) => (
                                        <Fragment key={i}>
                                            {i > 0 && <br />}
                                            {line}
                                        </Fragment>
                                    ))}
                                </h3>
                                <img
                                    src={assets[col.imageKey]}
                                    alt={col.imageAlt}
                                    className={cn(
                                        'mt-[2.6rem] rounded-[0.375rem] object-cover',
                                        inView && 'hde-scale-settle',
                                    )}
                                    style={{
                                        width: '25.8125rem',
                                        height: '13.5rem',
                                        backgroundColor: '#c4c4c4',
                                        animationDelay: `${index * 150 + 100}ms`,
                                    }}
                                />
                                <ItemList items={col.items} />
                            </div>
                        </ScrollReveal>
                    ))}
                </div>

                {/* Register Now – Free pill button */}
                <div className="flex justify-center pb-[3.75rem] pt-[4rem]">
                    <button
                        type="button"
                        onClick={onRegister}
                        className={`flex h-[3.125rem] items-center justify-center rounded-full bg-[#1e1e1e] px-[2.234rem] text-[1.058rem] font-normal text-white transition-colors hover:bg-gray-700 active:scale-[0.97] ${showRegisterButton ? '' : 'pointer-events-none invisible'}`}
                    >
                        Register Now – Free
                    </button>
                </div>
            </HDEDesktopFrame>
        </section>
    );
};
